import axios from 'axios';
import { supabase } from './supabase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

// Authorization header from the current Supabase session — used by endpoints
// that authenticate the dashboard user server-side (e.g. /websites).
async function authHeaders() {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

/**
 * Is this 401 worth one session refresh and one retry?
 *
 * Extracted so the decision can be tested without an HTTP mocking library:
 *
 *   • 401 only — other failures have their own handling below.
 *   • ONCE per request. `__authRetried` stops a server that answers 401 to a
 *     freshly minted token from becoming an infinite refresh loop.
 *   • never /auth/*. A 401 from a login is a wrong password, and relabelling
 *     that "your session has expired" is a lie at the one moment the user has
 *     no session to expire.
 */
export const shouldAttemptSessionRefresh = (error, config) =>
  error?.response?.status === 401
  && Boolean(config)
  && !config.__authRetried
  && !String(config.url || '').includes('/auth/');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // ✅ 30 second timeout for network requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Automatic retry with jittered exponential backoff ────────────────────────
// A single dropped packet on a flaky connection (e.g. spotty Wi-Fi on a laptop)
// should not hard-fail a login/connect. We retry transient failures only:
//   - network errors / timeouts (no HTTP response came back), and
//   - 502/503/504 (gateway/overload — server-side transient).
// We never retry 4xx (client errors) or 5xx other than the gateway codes.
//
// Safety: only retry idempotent requests. GET/HEAD are always safe. POSTs are
// retried only when explicitly opted in via `config.retry = true` — used for
// operations that are idempotent server-side (issue-connect-token returns the
// same token on repeat; verify-reset-eligibility is read-only).
const MAX_RETRIES = 3;

function isRetriableError(error) {
  // No response → network error or timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response) {
    return true;
  }
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
}

function isRetriableRequest(config) {
  if (!config) return false;
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get' || method === 'head') return true;
  return config.retry === true; // explicit opt-in for idempotent POSTs
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    if (config && isRetriableRequest(config) && isRetriableError(error)) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        // Exponential backoff with full jitter: ~0.3s, ~0.6s, ~1.2s (+ random)
        const base = 300 * Math.pow(2, config.__retryCount - 1);
        const delay = base + Math.floor(Math.random() * 200);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(config);
      }
    }

    // ── An expired session is not a data-loading failure ────────────────────
    // Endpoints that authenticate the dashboard user (/websites, and now
    // /users/content-growth) answer 401 once the Supabase access token lapses.
    // Nothing handled that, so every caller reported its own generic message —
    // the Websites page said "Failed to load websites", which reads as "this
    // feature is broken" when the real answer is "you need to sign in again".
    //
    // One refresh, then one retry. getSession() already refreshes a merely
    // expired token, so reaching here means the token the server saw was
    // rejected outright; an explicit refreshSession is the last thing worth
    // trying before telling the user to sign in.
    //
    // /auth/* is excluded: a 401 there is a wrong password, not a dead session,
    // and relabelling it "your session expired" would be a lie at the one
    // moment the user has no session to expire.
    if (shouldAttemptSessionRefresh(error, config)) {
      config.__authRetried = true;
      try {
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        const token = data?.session?.access_token;
        if (!refreshError && token) {
          config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
          return api(config);
        }
      } catch (_) { /* refresh unavailable — fall through to the message below */ }

      // Flagged so callers can distinguish "sign in again" from "we could not
      // load your data", which are different problems with different fixes.
      error.isAuthExpired = true;
      error.message = 'Your session has expired. Please sign in again.';
      return Promise.reject(error);
    }

    // Enhanced network error handling (after retries are exhausted)
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', error);
      error.isTimeout = true;
      error.message = 'Request timed out. Please check your connection and try again.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      console.error('Network error:', error);
      error.isNetworkError = true;
      error.message = 'Network error. Please check your internet connection.';
    }
    return Promise.reject(error);
  }
);

export class ApiService {
  // Auth
  async signup({ email, name, password, source = 'web' }) {
    const response = await api.post('/auth/signup', { email, name, password, source });
    return response.data; // { success, data } or { success: false, error }
  }

  // User Management
  async getUserByEmail(email) {
    try {
      const response = await api.post('/users/by-email', { email });
      return response.data.success ? response.data.user : null;
    } catch (error) {
      if (error.response?.status === 404) {
        const notFoundError = new Error('No account found for this email. Please sign up first.');
        notFoundError.code = 'USER_NOT_FOUND';
        throw notFoundError;
      }
      console.error('Failed to fetch user by email:', error);
      return null;
    }
  }

  async syncOAuthUser(provider, supabaseUser) {
    // user_metadata.provider_id is the Google sub ID (most reliable, present in JWT)
    // identities[0].id is a fallback if available, then Supabase UUID as last resort
    const oauthId =
      supabaseUser.user_metadata?.provider_id ||
      supabaseUser.identities?.[0]?.id ||
      supabaseUser.id;
    const response = await api.post('/auth/oauth-callback', {
      provider,
      user: {
        id: oauthId,
        email: supabaseUser.email,
        name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
        image: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || null,
      },
    });
    return response.data.success ? response.data.data.user : null;
  }

  async getUserById(userId) {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data.success ? response.data.user : null;
    } catch (error) {
      console.error('Failed to fetch user by ID:', error);
      return null;
    }
  }

  // Content Generation (for testing API keys)
  async generateContent(apiKey, siteCode, params) {
    try {
      const response = await api.post('/generation/active', params, {
        headers: {
          'x-api-key': apiKey,
          'x-site-code': siteCode,
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate content:', error);
      throw error;
    }
  }

  // Website Management (server requires the Supabase JWT — identity comes from
  // the token, not from user_id in the request)
  async getUserWebsites(userId) {
    try {
      const response = await api.get(`/websites/${userId}`, { headers: await authHeaders() });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch websites:', error);
      throw error;
    }
  }

  async addWebsite(websiteData) {
    try {
      const response = await api.post('/websites', websiteData, { headers: await authHeaders() });
      return response.data;
    } catch (error) {
      console.error('Failed to add website:', error);
      throw error;
    }
  }

  async deleteWebsite(websiteId) {
    try {
      const response = await api.delete(`/websites/${websiteId}`, { headers: await authHeaders() });
      return response.data;
    } catch (error) {
      console.error('Failed to delete website:', error);
      throw error;
    }
  }

  // Credit Management
  async getUserCredits(userId) {
    try {
      const response = await api.get(`/users/credits/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      throw error;
    }
  }

  /**
   * Real credit consumption per type (bug 12), counted from credit_transactions
   * rows that carry a generation_id — i.e. what was actually spent generating,
   * regardless of which bucket paid for it.
   */
  async getCreditUsage(userId) {
    try {
      const response = await api.get(`/users/credit-usage/${userId}`);
      return response.data;
    } catch (error) {
      // Non-fatal: useCreditBalances falls back to allotment arithmetic.
      console.error('Failed to fetch credit usage:', error);
      throw error;
    }
  }

  async getUserSubscription(userId) {
    try {
      const response = await api.get(`/users/subscription/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      throw error;
    }
  }

  // Analytics
  async getUserAnalytics(userId) {
    try {
      const response = await api.get(`/users/analytics/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      throw error;
    }
  }

  async getUserGenerations(userId, options = {}) {
    try {
      const params = new URLSearchParams(options).toString();
      const response = await api.get(`/users/generations/${userId}?${params}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch generations:', error);
      throw error;
    }
  }

  /**
   * Monthly article counts for the Overview "Content Growth" chart.
   *
   * ── WHAT THIS USED TO DO, AND WHY IT STOPPED WORKING ────────────────────
   * It queried `user_generations` through Supabase from the BROWSER, selecting
   * and filtering a `credit_type` column. That column does not exist on that
   * table, so every call came back 42703 ("column does not exist"), this method
   * logged and returned null, and the chart fell through to a second path that
   * was itself reading a response shape the server does not send. An account
   * with 126 articles was shown "No content generated yet", and nothing failed
   * loudly enough for anyone to notice.
   *
   * The aggregation now happens in SQL on the server. That fixes the bug and
   * removes the arrangement that caused it: the browser no longer needs to know
   * this table's schema, so the next column change cannot break the chart
   * silently. It also drops the 1000-row ceiling the client-side path had.
   *
   * Returns [{ month: 'YYYY-MM', count: N }] ascending, or null on failure so
   * the caller can leave the last good chart on screen. An empty ARRAY is a
   * real answer — a new account — and is not the same as null.
   */
  async getContentGrowthAggregated(userId) {
    try {
      // Authenticated: this is one customer's publishing history, and the
      // server checks the token against the id rather than trusting the path.
      const response = await api.get(`/users/content-growth/${userId}`, {
        headers: await authHeaders(),
      });
      const rows = response.data?.data;
      return Array.isArray(rows) ? rows : null;
    } catch (error) {
      console.error('Failed to fetch content growth:', error);
      return null;
    }
  }


  // REMOVED: getWebsiteIdsWithGenerations(userId)
  //
  // It queried user_generations to answer "is this site connected?", on the
  // stated grounds that `websites.last_connected_at` "was reverted". It was
  // not — the server stamps it on every authenticated plugin request and it
  // arrives with the websites payload. "Has ever generated content" cannot go
  // false when a plugin is disconnected, which is the bug it caused. See
  // utils/websiteStatus.js.

  async getUserWebsiteStats(userId) {
    try {
      const response = await api.get(`/users/website-stats/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch website stats:', error);
      throw error;
    }
  }

  // Stripe/Payment Management
  async getSubscriptionPlans(userId = null) {
    try {
      const url = userId ? `/stripe/plans?user_id=${userId}` : '/stripe/plans';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      throw error;
    }
  }

  async createCheckoutSession(userId, priceId, successUrl, cancelUrl) {
    try {
      const response = await api.post('/stripe/create-checkout-session', {
        user_id: userId,
        price_id: priceId,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw error;
    }
  }

  // Verify a completed Stripe Checkout session on return from the redirect.
  // Applies the purchase (credits/subscription) immediately and idempotently,
  // so the user doesn't have to wait on the async webhook.
  async verifyCheckoutSession(sessionId) {
    try {
      const response = await api.post('/stripe/verify-session', { session_id: sessionId });
      return response.data;
    } catch (error) {
      console.error('Failed to verify checkout session:', error);
      throw error;
    }
  }

  /**
   * Start a Stripe Checkout session for one credit package.
   *
   * `package_id` — 'small' | 'medium' | 'large' — replaces the old
   * (credit_type, quantity) pair. That pair asked for "10 content credits",
   * which the server has priced as a package since the three pools merged: the
   * credit_type was mapped onto the single ISEO pool and the quantity onto the
   * nearest package, so the caller could not actually tell what it was buying
   * or what it would cost. The package is the real unit of sale, and its price
   * comes back from getCreditPackages().
   */
  async buyCreditPackage(userId, packageId, successUrl, cancelUrl, paymentMethodId = null) {
    try {
      const response = await api.post('/stripe/buy-credits', {
        user_id: userId,
        package_id: packageId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        // Which saved card to charge, for a subscriber paying inside the modal.
        // Advisory: the server falls back to the account default, and the PRICE
        // is never taken from here — the tier is resolved server-side from the
        // plan, so this cannot be used to ask for the subscriber rate.
        ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to buy credits:', error);
      throw error;
    }
  }

  async getPieceEstimates(creditsList) {
    // THE "≈ N pieces" figure — calculated only by the server, and shared with
    // the WordPress plugin, so one credit amount reads the same everywhere.
    const response = await api.get('/credits/estimate', {
      params: { credits: creditsList.join(',') },
    });
    return response.data;
  }

  /**
   * The credit packages on sale, the per-piece rate, and the per-action price
   * table.
   *
   * One request for all three because they come from one config and are read
   * together: the storefront needs the packages, and the balance wording needs
   * the rate and the prices to say what a balance is worth.
   */
  async getCreditPackages(userId = null) {
    try {
      // `user_id` resolves the PRICE TIER server-side — a subscriber is quoted
      // the discounted rate. Without it the server serves the free (higher)
      // rate, which is the safe default: quoting the discount to someone who
      // will be charged more is the one outcome worth ruling out.
      const response = await api.get('/stripe/credit-packages', {
        params: userId ? { user_id: userId } : undefined,
      });
      return response.data;
    } catch (error) {
      // Advisory only — callers fall back to shipped defaults.
      console.error('Failed to fetch credit packages:', error);
      throw error;
    }
  }

  /**
   * Open Stripe's hosted Checkout for a FIRST subscription.
   *
   * The endpoint for "this user has no card yet". Card entry, the first
   * payment and the subscription all happen on one Stripe-hosted page, so the
   * user is never asked to save a payment method as a separate errand before
   * they are allowed to buy anything.
   *
   * `returnTo` is where a CANCELLED checkout puts the user back — echoed
   * through the URL rather than stored, because the redirect can complete in a
   * different tab or after a reload. Success always lands on Credits, where
   * Dashboard verifies the session and applies the purchase immediately rather
   * than waiting on the webhook.
   *
   * @param idempotencyKey one key per confirm action, reused across retries, so
   *   a double click cannot open two checkouts (and therefore cannot produce
   *   two subscriptions).
   */
  async createSubscriptionCheckout(userId, planId, { returnTo = '/credits', interval = 'month', idempotencyKey = null } = {}) {
    try {
      const back = encodeURIComponent(returnTo);
      const response = await api.post('/stripe/create-subscription-checkout', {
        user_id: userId,
        plan_id: planId,
        interval,
        // The server appends session_id={CHECKOUT_SESSION_ID} to this.
        success_url: `${window.location.origin}/credits?payment=success`,
        cancel_url: `${window.location.origin}/credits?payment=cancelled&return_to=${back}`,
        ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Failed to start subscription checkout:', error);
      throw error;
    }
  }

  /**
   * A SetupIntent for collecting a card IN PAGE via Stripe Elements (BILL-002).
   *
   * The returned client_secret is what lets Stripe.js confirm the card from the
   * browser. It is scoped to this one setup and is useless for anything else,
   * which is why it can safely be handed to the client at all.
   */
  async createSetupIntent(userId) {
    try {
      const response = await api.post('/stripe/create-setup-intent', { user_id: userId });
      return response.data;
    } catch (error) {
      console.error('Failed to create setup intent:', error);
      throw error;
    }
  }

  /**
   * @param idempotencyKey a key the CALLER generates once per confirm action
   *   and reuses across retries. Two submissions carrying the same key produce
   *   one subscription and one charge, which is what makes a double-clicked
   *   button safe (BILL-002).
   */
  async subscribeToPlan(userId, planId, idempotencyKey = null, interval = 'month') {
    try {
      const response = await api.post('/stripe/subscribe-to-plan', {
        user_id: userId,
        plan_id: planId,
        interval,
        ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Failed to subscribe to plan:', error);
      throw error;
    }
  }

  /**
   * Price a plan change WITHOUT committing to it, so the confirm dialog can
   * show the real prorated amount before the user agrees to pay it (bug 6).
   *
   * The returned `proration_date` must be handed back to changePlan() — it pins
   * the instant the quote was priced at, so the customer is charged exactly
   * what they were shown rather than a figure that drifted by however long they
   * spent reading the dialog.
   */
  async previewPlanChange(userId, newPlanId) {
    try {
      const response = await api.post('/stripe/preview-plan-change', {
        user_id: userId,
        new_plan_id: newPlanId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to preview plan change:', error);
      throw error;
    }
  }

  async changePlan(userId, newPlanId, prorationDate = null, options = {}) {
    try {
      const response = await api.post('/stripe/change-plan', {
        user_id: userId,
        new_plan_id: newPlanId,
        // Honour the quote the user was shown. Omitted when they never saw a
        // preview (the server then prices at "now", as before).
        ...(prorationDate ? { proration_date: prorationDate } : {}),
        // Ties the charge to the exact quote the user accepted, so the server
        // can prove afterwards what was disclosed (BILL-003).
        ...(options.disclosureId ? { disclosure_id: options.disclosureId } : {}),
        // One key per confirm action, reused across retries: a double-submit
        // becomes one charge rather than two (BILL-002).
        ...(options.idempotencyKey ? { idempotency_key: options.idempotencyKey } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Failed to change plan:', error);
      throw error;
    }
  }

  /**
   * Billing history for the "Recent Transactions" list — Stripe invoices merged
   * with credit purchases and plan grants (bug 7).
   */
  async getBillingHistory(userId, { limit = 20, cursor = null } = {}) {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      // Keyset pagination: the server pages on (occurred_at, id) so rows
      // written while the user is reading cannot repeat or be skipped.
      if (cursor) params.set('cursor', cursor);
      const response = await api.get(`/stripe/billing-history/${userId}?${params}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch billing history:', error);
      throw error;
    }
  }

  /**
   * Promote a saved card to default if the customer has cards but no default.
   * Called right after returning from adding a card, so the upgrade path works
   * immediately instead of waiting on the setup_intent.succeeded webhook (bug 3).
   */
  async ensureDefaultPaymentMethod(userId) {
    try {
      const response = await api.post('/stripe/ensure-default-payment-method', { user_id: userId });
      return response.data;
    } catch (error) {
      // Best-effort: the server self-heals on the next payment-methods read too.
      console.error('Failed to ensure default payment method:', error);
      return { success: false };
    }
  }

  async pauseSubscription(userId) {
    try {
      const response = await api.post('/stripe/pause-subscription', {
        user_id: userId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to pause subscription:', error);
      throw error;
    }
  }

  async resumeSubscription(userId) {
    try {
      const response = await api.post('/stripe/resume-subscription', {
        user_id: userId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to resume subscription:', error);
      throw error;
    }
  }

  async cancelSubscription(userId) {
    try {
      const response = await api.post('/stripe/cancel-subscription', {
        user_id: userId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error;
    }
  }

  // Payment Method Management
  async getPaymentMethods(userId) {
    try {
      const response = await api.get(`/stripe/payment-methods/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      throw error;
    }
  }

  /**
   * Start Stripe's hosted card-setup flow.
   *
   * `returnTo` is the in-app path to come back to (BILL-001: a user who goes
   * off to add a card mid-upgrade should not be stranded in Settings having to
   * find their way back). It was previously hardcoded to /settings, so there
   * was no way to express "bring me back here" even when the caller knew.
   *
   * Both URLs land on the same page, distinguished by the `setup` parameter,
   * which Dashboard reads. The return path is echoed rather than stored,
   * because the redirect can complete in a different tab or after a reload.
   */
  async createSetupSession(userId, returnTo = '/settings') {
    try {
      const back = encodeURIComponent(returnTo);
      const response = await api.post('/stripe/create-setup-session', {
        user_id: userId,
        success_url: `${window.location.origin}/settings?setup=success&return_to=${back}`,
        cancel_url: `${window.location.origin}/settings?setup=cancelled&return_to=${back}`
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create setup session:', error);
      throw error;
    }
  }

  /**
   * Open Stripe's billing portal — where a user updates their billing address.
   *
   * The answer to "how will the user be able to update their address if needed?"
   * once Stripe took over collecting it. We deliberately do NOT keep a form of
   * our own writing through to Stripe: that would be a second copy of the
   * address, and the two drift the moment it is edited on Stripe's side —
   * leaving our copy wrong on exactly the paperwork it exists for.
   *
   * The portal also brings card management and invoice history, and handles SCA
   * where the region requires it.
   */
  async openBillingPortal(userId, returnTo = '/settings') {
    try {
      const response = await api.post('/stripe/billing-portal', {
        user_id: userId,
        return_url: `${window.location.origin}${returnTo}`,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to open the billing portal:', error);
      throw error;
    }
  }

  async removePaymentMethod(userId, paymentMethodId) {
    try {
      const response = await api.delete(`/stripe/payment-method/${userId}/${paymentMethodId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove payment method:', error);
      throw error;
    }
  }

  async setDefaultPaymentMethod(userId, paymentMethodId) {
    try {
      const response = await api.post('/stripe/set-default-payment-method', {
        user_id: userId,
        payment_method_id: paymentMethodId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      throw error;
    }
  }

  // ==================== ACCOUNT MANAGEMENT ====================

  async updateProfile(userId, data) {
    try {
      const response = await api.patch(`/users/profile/${userId}`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }

  // ── Address on file, for tax purposes (task3.md 4.2) ────────────────────
  // Its own endpoint rather than part of the profile PATCH, so the Settings
  // form and the onboarding prompt share one contract and the "do we have an
  // address yet?" check is a single cheap read.

  async getUserAddress(userId) {
    try {
      const response = await api.get(`/users/address/${userId}`);
      return response.data;
    } catch (error) {
      // Never log the response — it carries a postal address.
      console.error('Failed to fetch address');
      throw error;
    }
  }

  async saveUserAddress(userId, address) {
    try {
      const response = await api.put(`/users/address/${userId}`, {
        line1: address.line1,
        line2: address.line2 || null,
        city: address.city,
        state: address.state || null,
        postal_code: address.postal_code || null,
        country: address.country,
      });
      return response.data;
    } catch (error) {
      // Deliberately logs only that it failed, never the payload (PII).
      console.error('Failed to save address');
      throw error;
    }
  }

  async regenerateApiKey(userId) {
    try {
      const response = await api.post(`/users/regenerate-api-key/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
      throw error;
    }
  }

  // ==================== SUPPORT TICKETS ====================

  /**
   * Create a new support ticket
   */
  async createSupportTicket(ticketData) {
    try {
      const response = await api.post('/tickets', ticketData);
      return response.data;
    } catch (error) {
      console.error('Failed to create support ticket:', error);
      throw error;
    }
  }

  /**
   * Get all tickets for the current user
   */
  async getUserTickets(userId, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.status) params.append('status', options.status);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.offset) params.append('offset', options.offset.toString());
      
      const queryString = params.toString();
      const url = `/tickets/user/${userId}${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user tickets:', error);
      throw error;
    }
  }

  /**
   * Get a single ticket with its messages
   */
  async getTicketById(ticketId) {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
      throw error;
    }
  }

  /**
   * Add a message to a ticket
   */
  async addTicketMessage(ticketId, messageData) {
    try {
      const response = await api.post(`/tickets/${ticketId}/messages`, messageData);
      return response.data;
    } catch (error) {
      console.error('Failed to add ticket message:', error);
      throw error;
    }
  }

  /**
   * Upload files to a ticket
   */
  async uploadTicketFiles(ticketId, files, uploadedByType = 'user', uploadedById = '0', messageId = null) {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('uploaded_by_type', uploadedByType);
      formData.append('uploaded_by_id', uploadedById);
      if (messageId) formData.append('message_id', messageId.toString());

      const response = await api.post(`/tickets/${ticketId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      return response.data;
    } catch (error) {
      console.error('Failed to upload ticket files:', error);
      throw error;
    }
  }

  /**
   * Get ticket attachments
   */
  async getTicketAttachments(ticketId) {
    try {
      const response = await api.get(`/tickets/${ticketId}/attachments`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch ticket attachments:', error);
      throw error;
    }
  }

  // Password Reset Eligibility
  async verifyResetEligibility(email) {
    try {
      // Read-only → safe to retry on transient network failures
      const response = await api.post('/auth/verify-reset-eligibility', { email }, { retry: true });
      return response.data;
    } catch (error) {
      console.error('Failed to verify reset eligibility:', error);
      return { success: false, eligible: false, message: 'Unable to verify account. Please try again.' };
    }
  }

  // Set password for OAuth user (syncs password_hash to backend)
  async setPassword(email, password) {
    try {
      const response = await api.post('/auth/set-password', { email, password });
      return response.data;
    } catch (error) {
      console.error('Failed to sync password to backend:', error);
      throw error;
    }
  }

  // ==================== PLUGIN CONNECT ====================

  /**
   * Issue a short-lived connect token for the WordPress plugin.
   * Called from PluginConnect.jsx after the user authenticates.
   *
   * @param {string} site_domain  The domain the plugin reported (e.g. "example.com")
   * @param {string} jwt          Supabase access_token from the active session
   */
  async issueConnectToken(site_domain, jwt) {
    const response = await api.post(
      '/auth/issue-connect-token',
      { site_domain, source: 'plugin' },
      // Idempotent server-side (returns the same token on repeat) → safe to retry
      { headers: { Authorization: `Bearer ${jwt}` }, retry: true }
    );
    // Throw on non-success so callers can catch cleanly
    if (!response.data.success) {
      const err = new Error(response.data.error || 'Failed to issue connect token');
      err.code = response.data.code;
      throw err;
    }
    return response.data.data; // { connect_token, trial_status, trial_ends_at }
  }
}

export const apiService = new ApiService();
export default apiService;